import { Hyperlink } from "@Light/components/page/Hyperlink";
import { MutationButton } from "@Light/components/page/MutationButton";
import { PageBody } from "@Light/components/page/PageBody";
import { getFullDate } from "@Light/components/system/Datepicker/helpers";
import { ErrorMessage, Field } from "@Light/components/system/Fieldset";
import { Input } from "@Light/components/system/Input";
import type { RequirementsPatch } from "@Light/services/lightTypes";
import {
  formatPhoneNumber,
  validatePhoneNumber,
} from "@Light/utils/phoneNumber";
import { Phone } from "@untitledui/icons";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { EnrollHelpButtonFooter } from "../EnrollHelpButtonFooter";
import { FieldLabel } from "../FieldLabel";
import { useEnrollment } from "../flow/context";
import { isUnsatisfied, maybeRequirementValue } from "../flow/screen";
import { useEnrollmentStepper } from "../flow/useEnrollmentStepper";
import { EnrollRoute } from "../routes";
import { useEnrollmentUtilityConfiguration } from "../useEnrollmentUtilityConfiguration";
import { BirthdayFields } from "./BirthdayFields";
import { FrozenCreditBottomSheet } from "./FrozenCreditBottomSheet";
import { isCompleteSsn, SsnInput } from "./SsnInput";

type CreditCheckForm = {
  phone_number: string;
  birth_month: string;
  birth_day: string;
  birth_year: string;
  ssn: string;
};

export const CreditCheck = () => {
  const { next, back, nextMutation, requirements } = useEnrollment();
  const { needsDateOfBirth } = useEnrollmentUtilityConfiguration();
  const stepper = useEnrollmentStepper(EnrollRoute.CreditCheck);

  const phoneNumber = maybeRequirementValue(requirements.phone_number)?.value;
  const dob = maybeRequirementValue(requirements.dob);
  const hasStoredSsn = !!maybeRequirementValue(requirements.ssn);
  const serviceStartDate = maybeRequirementValue(
    requirements.service_start_date,
  )?.value;

  const needsCreditCheck = !!requirements.ssn;
  const [isShowingDateOfBirth] = useState(
    () => needsDateOfBirth || isUnsatisfied(requirements.dob),
  );

  const [frozenSheetOpen, setFrozenSheetOpen] = useState(false);

  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors, dirtyFields },
  } = useForm<CreditCheckForm>({
    reValidateMode: "onSubmit",
    defaultValues: {
      phone_number: phoneNumber ? formatPhoneNumber(phoneNumber) : "",
      birth_month: dob ? String(dob.month).padStart(2, "0") : "",
      birth_day: dob ? String(dob.day).padStart(2, "0") : "",
      birth_year: dob?.year ? String(dob.year) : "",
      ssn: "",
    },
  });

  // Only patch modified fields
  const onSubmit = handleSubmit(async (data) => {
    const patch: RequirementsPatch = {};

    if (dirtyFields.phone_number) {
      patch.phone_number = { value: data.phone_number };
    }

    if (
      dirtyFields.birth_month ||
      dirtyFields.birth_day ||
      dirtyFields.birth_year
    ) {
      patch.dob = {
        value: `${data.birth_year}-${data.birth_month}-${data.birth_day}`,
      };
    }

    if (data.ssn) {
      patch.ssn = { value: data.ssn, agree_to_terms: true };
    }

    await next(Object.keys(patch).length > 0 ? patch : undefined);
  });

  return (
    <form onSubmit={onSubmit}>
      <PageBody
        onBack={back}
        title={
          needsCreditCheck
            ? "We need to verify your credit"
            : isShowingDateOfBirth
              ? "We just need a few more details to check your identity"
              : "We just need one more detail to check your identity"
        }
        subtitle={
          needsCreditCheck ? (
            <>
              We&rsquo;ll do a soft credit check that won&rsquo;t affect your
              credit score.{" "}
              <button
                type="button"
                className="cursor-pointer underline"
                onClick={() => {
                  setFrozenSheetOpen(true);
                }}
                data-testid="credit-frozen-open"
              >
                What if I freeze my credit?
              </button>
            </>
          ) : undefined
        }
        stepper={stepper}
        footer={
          <EnrollHelpButtonFooter
            disclaimer={
              needsCreditCheck ? (
                <p className="text-xs text-secondary">
                  By selecting &ldquo;I agree&rdquo;, you authorize Light under
                  the Fair Credit Reporting Act (FCRA) to obtain your credit
                  report solely to determine eligibility for electric service.
                </p>
              ) : undefined
            }
          >
            <MutationButton
              size="lg"
              mutation={nextMutation}
              mutateButtonText={
                needsCreditCheck ? "I agree" : "Save and continue"
              }
              errorMessage="Failed to submit your details. Please try again later"
              data-testid="credit-check-submit"
            />
          </EnrollHelpButtonFooter>
        }
      >
        <div className="flex flex-col gap-6">
          <Field className="flex w-full flex-col gap-1.5">
            <FieldLabel>What&rsquo;s your phone number?</FieldLabel>
            <Input
              type="tel"
              icon={Phone}
              placeholder="(555) 555-5555"
              isError={!!errors.phone_number}
              data-testid="phone-number-input"
              {...register("phone_number", {
                required: true,
                onChange: (e) => {
                  setValue("phone_number", formatPhoneNumber(e.target.value), {
                    shouldDirty: true,
                  });
                },
                validate: (value) => !value || validatePhoneNumber(value),
              })}
            />
            <ErrorMessage error={errors.phone_number?.message} />
          </Field>

          {isShowingDateOfBirth && (
            <BirthdayFields
              label={<>What&rsquo;s your birthday?</>}
              month={register("birth_month", { required: true })}
              day={register("birth_day", { required: true })}
              year={register("birth_year", { required: true })}
              errors={{
                month: !!errors.birth_month,
                day: !!errors.birth_day,
                year: !!errors.birth_year,
              }}
            />
          )}

          {needsCreditCheck && (
            <Field className="flex w-full flex-col gap-1.5">
              <FieldLabel>What&rsquo;s your social security number?</FieldLabel>
              <Controller
                control={control}
                name="ssn"
                rules={{
                  validate: (value) =>
                    value
                      ? isCompleteSsn(value) ||
                        "Please enter a valid 9-digit Social Security number."
                      : // An untouched field keeps the number already on file.
                        hasStoredSsn,
                }}
                render={({ field, fieldState }) => (
                  <SsnInput
                    hasStoredSsn={hasStoredSsn}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    isError={!!fieldState.error}
                  />
                )}
              />
              <ErrorMessage error={errors.ssn?.message} />
              <p className="text-sm text-tertiary">
                Data is handled securely per our{" "}
                <Hyperlink
                  href="https://www.poweredbylight.com/privacy"
                  target="_blank"
                >
                  Privacy Policy
                </Hyperlink>
                .
              </p>
            </Field>
          )}
        </div>
      </PageBody>

      <FrozenCreditBottomSheet
        open={frozenSheetOpen}
        onClose={() => {
          setFrozenSheetOpen(false);
        }}
        deadline={serviceStartDate && getFullDate(serviceStartDate)}
      />
    </form>
  );
};
